import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

import { lockEnabled, verify } from './biometrics';

/**
 * Where this copy of the application is running, and how it proves who it is.
 *
 * In a browser the answer is "nowhere in particular": the page and the API are
 * served by the same host, the session lives in a cookie the page cannot read,
 * and none of what follows applies.
 *
 * In the Android application the page is served by the WebView itself from
 * `https://localhost`, so every call to the server is cross-site. The session
 * cookie is `SameSite=Lax` and is simply never sent — deliberately, because
 * that is what stops another site from acting as the signed-in user. The
 * application therefore holds the session token itself and sends it in an
 * `Authorization` header, which the backend has accepted from the beginning
 * (`deps._extract_token`).
 *
 * The token is kept in `@capacitor/preferences`, which is Android's private
 * per-application storage: readable by this application and by root, and by
 * nothing else on the device. It is not a secret worth encrypting on top of
 * that — it is a session, it expires, and it can be revoked from the account
 * screen on any other device.
 */

export const isNative = Capacitor.isNativePlatform();

// Published to the stylesheet, so that the handful of rules which only make
// sense in one of the two — the camera preview that the application never
// fills, for one — can be written as CSS instead of threaded through props.
if (isNative) document.documentElement.dataset.native = 'true';

/** Which server the application talks to.
 *
 *  Fixed at build time rather than typed in on first run: this application is
 *  built for one workshop and one server, and an address field on the login
 *  screen is a field to get wrong. `VITE_API_ORIGIN` moves it for a second
 *  installation. Empty in a browser, where the API is simply here. */
const origin = isNative ? (import.meta.env.VITE_API_ORIGIN ?? 'https://storage.m8s.ru') : '';

export const apiBase = `${origin}/api/v1`;

const TOKEN_KEY = 'storageapp.session';

/** Shown by the phone above its own fingerprint prompt. Not translated here:
 *  this runs before React, i18next and the stored language are up, and a prompt
 *  that arrives half a second late is a prompt people press past. */
const LOCK_REASON = 'Вход в «Склад»';

let token: string | null = null;
/** A session that exists but was not unlocked. It stays in storage. */
let withheld = false;

export function sessionToken(): string | null {
  return token;
}

/**
 * Read back the session left by the last run.
 *
 * Called before the first render, so that the application does not flash its
 * login screen at somebody who is already signed in — the profile request that
 * decides that has to be able to carry the token.
 */
export async function restoreSession(): Promise<void> {
  if (!isNative) return;

  let stored: string | null = null;
  try {
    stored = (await Preferences.get({ key: TOKEN_KEY })).value;
  } catch {
    // No stored session is the normal case on a first run, and a storage that
    // will not open is not a reason to refuse to start: the worst outcome is
    // being asked to sign in again.
    return;
  }
  if (!stored) return;

  // The phone's own lock, if it was asked for. Held back rather than thrown
  // away when it refuses: the session is still good, and the next start — or
  // the next finger — will use it.
  if (await lockEnabled()) {
    const passed = await verify(LOCK_REASON);
    if (!passed) {
      withheld = true;
      return;
    }
  }

  token = stored;
}

/**
 * The server refused the credential we sent.
 *
 * Only clears a token that was actually offered. A 401 from a request that
 * carried nothing says nothing about the stored session, and forgetting it
 * there would mean one cancelled fingerprint prompt logs the phone out for
 * good.
 */
export async function clearRejectedSession(): Promise<void> {
  if (withheld || !token) return;
  await rememberSession(null);
}

/** Keep, or forget, the session. `null` on signing out. */
export async function rememberSession(value: string | null): Promise<void> {
  token = value;
  withheld = false;
  if (!isNative) return;
  try {
    if (value) await Preferences.set({ key: TOKEN_KEY, value });
    else await Preferences.remove({ key: TOKEN_KEY });
  } catch {
    // Kept in memory regardless, so the session in hand still works.
  }
}
